import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { buttonVariants } from "@/components/ui/button";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, ErrorState } from "@/components/States";
import { Chip } from "@/components/ui/chip";
import { Pagination } from "@/components/ui/pagination";
import { TableSkeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from "@/components/ui/table";
import { isApiError } from "@/lib/api";
import type { RadarLedgerRow } from "@/lib/types.radar";
import { LEDGER_PAGE_LIMIT, useRadarLedgerPage } from "./useRadar";

function RecordedAt({ value }: { value: string }) {
  const date = new Date(value);
  return <time dateTime={value} title={value}>{Number.isNaN(date.valueOf()) ? value : date.toLocaleString("en-GB", { timeZone: "UTC", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) + " UTC"}</time>;
}

function ReceiptOutcome({ status }: { status: string }) {
  const outcome = status === "delivered"
    ? { label: "Delivered", detail: "The receiving system confirmed the lead was created or updated." }
    : status === "acknowledged_stale"
      ? { label: "Older event received", detail: "The receiving system already held newer source data." }
      : { label: "Outcome unknown", detail: "A receipt was recorded, but a created or updated lead is not confirmed." };
  return <div className="flex flex-col items-start gap-1"><Chip signal="ctx">{outcome.label}</Chip><p className="max-w-sm text-xs text-ink-3">{outcome.detail}</p></div>;
}

function rowKey(row: RadarLedgerRow): string {
  return `${row.provider_uuid}:${row.high_intent_campaign_key}:${row.delivered_at}`;
}

function DeliveryLedger() {
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const page = cursors.length - 1;
  const pager = useRef<HTMLDivElement>(null);
  const pendingFocus = useRef<"Next page" | "Previous page" | null>(null);
  const ledger = useRadarLedgerPage(cursors[page]);
  const denied = ledger.isError && isApiError(ledger.error) && ledger.error.httpStatus === 403;
  const rows = ledger.isSuccess ? ledger.data.ledger : [];

  useEffect(() => {
    if (!pendingFocus.current || ledger.isFetching) return;
    if (ledger.isError) { pendingFocus.current = null; return; }
    if (!ledger.isSuccess) return;
    const direction = pendingFocus.current;
    pendingFocus.current = null;
    if (document.activeElement !== document.body && !pager.current?.contains(document.activeElement)) return;
    const preferred = pager.current?.querySelector<HTMLButtonElement>(`button[aria-label="${direction}"]:not(:disabled)`);
    const available = preferred ?? pager.current?.querySelector<HTMLButtonElement>("button:not(:disabled)");
    available?.focus();
  }, [ledger.isSuccess, ledger.isError, ledger.isFetching, page]);

  return (
    <section className="flex flex-col gap-5" aria-labelledby="delivery-heading">
      <div>
        <h2 id="delivery-heading" className="type-app-h text-xl font-semibold text-ink">Delivery</h2>
        <p className="mt-1 text-sm text-ink-2">Across your assigned campaigns</p>
        <p className="mt-2 max-w-3xl text-sm text-ink-3">Receiver receipts for each lead, including older events and unconfirmed outcomes. These records do not confirm email or DM outreach.</p>
      </div>
      {ledger.isLoading ? <TableSkeleton label="Loading delivery records…" rows={5} columns={4} /> : null}
      {ledger.isError && !denied ? <ErrorState error={ledger.error} onRetry={() => void ledger.refetch()} /> : null}
      {denied ? <EmptyState title="You do not have permission to view delivery records." body="Ask your RADAR administrator to review access for the selected organisation." /> : null}
      {ledger.isSuccess && rows.length === 0 ? <EmptyState title={page === 0 ? "No delivery records yet" : "No records on this page"} body="No records were returned for this organisation's assigned campaigns. Delivery history appears here when it is recorded." action={<Link to="/high-intent" className={buttonVariants({ variant: "secondary", size: "lg" })}>View campaigns</Link>} /> : null}
      {ledger.isSuccess && rows.length > 0 ? <>
        <p className="text-xs text-ink-3" aria-live="polite">{rows.length} records on this page · Up to {LEDGER_PAGE_LIMIT} per page</p>
        <div className="inv-card hidden overflow-x-auto md:block">
          <Table aria-label="Delivery records desktop view">
            <TableHead><TableRow><TableHeaderCell>Lead reference</TableHeaderCell><TableHeaderCell>Campaign</TableHeaderCell><TableHeaderCell>Status</TableHeaderCell><TableHeaderCell>Receipt recorded</TableHeaderCell></TableRow></TableHead>
            <TableBody>{rows.map((row) => <TableRow key={rowKey(row)}>
              <TableCell className="inv-mono text-xs">{row.provider_uuid}</TableCell>
              <TableCell>{row.high_intent_campaign_key.replaceAll("_", " ")}</TableCell>
              <TableCell><ReceiptOutcome status={row.delivery_status} /></TableCell>
              <TableCell className="whitespace-nowrap text-xs"><RecordedAt value={row.delivered_at} /></TableCell>
            </TableRow>)}</TableBody>
          </Table>
        </div>
        <ul className="grid gap-3 md:hidden" aria-label="Delivery records mobile view">
          {rows.map((row) => <li key={rowKey(row)} className="inv-card flex flex-col gap-3 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3"><h3 className="font-medium text-ink">{row.high_intent_campaign_key.replaceAll("_", " ")}</h3><ReceiptOutcome status={row.delivery_status} /></div>
            <dl className="flex flex-col gap-3 text-sm"><div><dt className="inv-label">Lead reference</dt><dd className="inv-mono mt-1 break-all text-xs">{row.provider_uuid}</dd></div><div><dt className="inv-label">Receipt recorded</dt><dd className="mt-1 text-xs text-ink-2"><RecordedAt value={row.delivered_at} /></dd></div></dl>
          </li>)}
        </ul>
      </> : null}
      <div ref={pager}>{ledger.isSuccess && (rows.length > 0 || page > 0) ? <Pagination label="Delivery pagination" page={page} hasPrevious={page > 0 && !ledger.isFetching} hasNext={ledger.data.next_cursor !== null && !ledger.isFetching}
        onPrevious={() => { pendingFocus.current = "Previous page"; setCursors((current) => current.slice(0, -1)); }}
        onNext={() => { if (ledger.data.next_cursor !== null) { pendingFocus.current = "Next page"; setCursors((current) => [...current, ledger.data.next_cursor!]); } }} /> : null}</div>
    </section>
  );
}

export function DeliveryPage() {
  const { orgId } = useSession();
  return <DeliveryLedger key={orgId} />;
}
